/******
 * This file was generated by a Rake task.
 * Do not modify this file manually.
 * Add changes to the `Order.ts` file.
 ******/
// oxlint-disable-next-line no-unused-vars
import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
dayjs.extend(timezone);

import { Order } from "./Order";

import { ActivitiesOrder } from "./ActivitiesOrder";
import { Audit } from "./Audit";
import { Comment } from "./Comment";
import { Delivery } from "./Delivery";
import { Material } from "./Material";
import { OrderCheckIn } from "./OrderCheckIn";
import { Project } from "./Project";

export type leadTimeUnitType = "days" | "weeks" | "months" | "years";
export type status =
    | "needs_roj"
    | "needs_lead_time"
    | "needs_material_submitted"
    | "needs_material_approved"
    | "needs_ordered"
    | "needs_scheduled"
    | "needs_received"
    | "complete";

export const leadTimeUnitTypeValues = { days: 0, weeks: 1, months: 2, years: 3 };
export const statusValues = {
    needs_roj: 5,
    needs_lead_time: 10,
    needs_material_submitted: 15,
    needs_material_approved: 20,
    needs_ordered: 25,
    needs_scheduled: 30,
    needs_received: 35,
    complete: 40,
};

export abstract class OrderModel {
    id: number;
    activityIds: string[] | null;
    anticipatedOnJobsite: Dayjs | null;
    anticipatedOrder: Dayjs | null;
    approveBy: Dayjs | null;
    archived: boolean | null;
    checkInFrequency: number | null;
    createdAt: Dayjs;
    cushion: number | null;
    deliveryId: number | null;
    leadTime: number | null;
    leadTimeUnitType: leadTimeUnitType;
    leadTimeUpdated: Dayjs | null;
    materialId: number | null;
    needsCheckIn: boolean;
    orderBy: Dayjs | null;
    orderName: string | null;
    ordered: Dayjs | null;
    orderedUpdated: Dayjs | null;
    projectId: number | null;
    quantity: number;
    requiredOnJobsite: Dayjs | null;
    requiredOnJobsiteUpdated: Dayjs | null;
    serialNumber: number | null;
    shakeoutDays: number | null;
    status: status;
    submitBy: Dayjs | null;
    updatedAt: Dayjs;
    displayName: string | null;
    activitiesOrders: ActivitiesOrder[] | undefined;
    audits: Audit[] | undefined;
    comments: Comment[] | undefined;
    delivery: Delivery | null | undefined;
    material: Material | null | undefined;
    orderCheckIns: OrderCheckIn[] | undefined;
    project: Project | null | undefined;
    _destroy: boolean | undefined;

    /* eslint-disable max-statements */
    constructor(
        id: number,
        activityIds: string[] | null,
        anticipatedOnJobsite: Dayjs | null,
        anticipatedOrder: Dayjs | null,
        approveBy: Dayjs | null,
        archived: boolean | null,
        checkInFrequency: number | null,
        createdAt: Dayjs,
        cushion: number | null,
        deliveryId: number | null,
        leadTime: number | null,
        leadTimeUnitType: leadTimeUnitType,
        leadTimeUpdated: Dayjs | null,
        materialId: number | null,
        needsCheckIn: boolean,
        orderBy: Dayjs | null,
        orderName: string | null,
        ordered: Dayjs | null,
        orderedUpdated: Dayjs | null,
        projectId: number | null,
        quantity: number,
        requiredOnJobsite: Dayjs | null,
        requiredOnJobsiteUpdated: Dayjs | null,
        serialNumber: number | null,
        shakeoutDays: number | null,
        status: status,
        submitBy: Dayjs | null,
        updatedAt: Dayjs,
        displayName: string | null,
        activitiesOrders: ActivitiesOrder[] | undefined,
        audits: Audit[] | undefined,
        comments: Comment[] | undefined,
        delivery: Delivery | null | undefined,
        material: Material | null | undefined,
        orderCheckIns: OrderCheckIn[] | undefined,
        project: Project | null | undefined
    ) {
        this.id = id;
        this.activityIds = activityIds;
        this.anticipatedOnJobsite = anticipatedOnJobsite;
        this.anticipatedOrder = anticipatedOrder;
        this.approveBy = approveBy;
        this.archived = archived;
        this.checkInFrequency = checkInFrequency;
        this.createdAt = createdAt;
        this.cushion = cushion;
        this.deliveryId = deliveryId;
        this.leadTime = leadTime;
        this.leadTimeUnitType = leadTimeUnitType;
        this.leadTimeUpdated = leadTimeUpdated;
        this.materialId = materialId;
        this.needsCheckIn = needsCheckIn;
        this.orderBy = orderBy;
        this.orderName = orderName;
        this.ordered = ordered;
        this.orderedUpdated = orderedUpdated;
        this.projectId = projectId;
        this.quantity = quantity;
        this.requiredOnJobsite = requiredOnJobsite;
        this.requiredOnJobsiteUpdated = requiredOnJobsiteUpdated;
        this.serialNumber = serialNumber;
        this.shakeoutDays = shakeoutDays;
        this.status = status;
        this.submitBy = submitBy;
        this.updatedAt = updatedAt;
        this.displayName = displayName;
        this.activitiesOrders = activitiesOrders;
        this.audits = audits;
        this.comments = comments;
        this.delivery = delivery;
        this.material = material;
        this.orderCheckIns = orderCheckIns;
        this.project = project;
    }
    /* eslint-enable max-statements */

    /** Rails `alias_attribute :name, :order_name` */
    get name(): string | null {
        return this.orderName;
    }

    set name(value: string | null) {
        this.orderName = value;
    }

    // oxlint-disable-next-line complexity
    static fromApiObject(json: any, timezone: string): Order {
        const newObject = new Order(
            json["id"],
            json["activity_ids"],
            json["anticipated_on_jobsite"] ? dayjs(json["anticipated_on_jobsite"]).tz(timezone) : null,
            json["anticipated_order"] ? dayjs(json["anticipated_order"]).tz(timezone) : null,
            json["approve_by"] ? dayjs(json["approve_by"]).tz(timezone) : null,
            json["archived"],
            json["check_in_frequency"],
            dayjs(json["created_at"]).tz(timezone),
            json["cushion"],
            json["delivery_id"],
            json["lead_time"],
            json["lead_time_unit_type"],
            json["lead_time_updated"] ? dayjs(json["lead_time_updated"]).tz(timezone) : null,
            json["material_id"],
            json["needs_check_in"],
            json["order_by"] ? dayjs(json["order_by"]).tz(timezone) : null,
            json["order_name"],
            json["ordered"] ? dayjs(json["ordered"]).tz(timezone) : null,
            json["ordered_updated"] ? dayjs(json["ordered_updated"]).tz(timezone) : null,
            json["project_id"],
            json["quantity"],
            json["required_on_jobsite"] ? dayjs(json["required_on_jobsite"]).tz(timezone) : null,
            json["required_on_jobsite_updated"] ? dayjs(json["required_on_jobsite_updated"]).tz(timezone) : null,
            json["serial_number"],
            json["shakeout_days"],
            json["status"],
            json["submit_by"] ? dayjs(json["submit_by"]).tz(timezone) : null,
            dayjs(json["updated_at"]).tz(timezone),
            json["display_name"],
            json["activities_orders"]?.map((item: any) => ActivitiesOrder.fromApiObject(item, timezone)),
            json["audits"]?.map((item: any) => Audit.fromApiObject(item, timezone)),
            json["comments"]?.map((item: any) => Comment.fromApiObject(item, timezone)),
            json["delivery"] == null ? json["delivery"] : Delivery.fromApiObject(json["delivery"], timezone),
            json["material"] == null ? json["material"] : Material.fromApiObject(json["material"], timezone),
            json["order_check_ins"]?.map((item: any) => OrderCheckIn.fromApiObject(item, timezone)),
            json["project"] == null ? json["project"] : Project.fromApiObject(json["project"], timezone)
        );

        return newObject;
    }

    // oxlint-disable-next-line complexity
    toApiObject(visited = new Set<any>()): any {
        if (visited.has(this)) {
            // Return null, undefined, or primary key to break the cycle
            return undefined;
        }
        visited.add(this);

        return {
            id: this.id,
            activity_ids: this.activityIds,
            anticipated_on_jobsite: this.anticipatedOnJobsite,
            anticipated_order: this.anticipatedOrder,
            approve_by: this.approveBy,
            archived: this.archived,
            check_in_frequency: this.checkInFrequency,
            created_at: this.createdAt,
            cushion: this.cushion,
            delivery_id: this.deliveryId,
            lead_time: this.leadTime,
            lead_time_unit_type: this.leadTimeUnitType,
            lead_time_updated: this.leadTimeUpdated,
            material_id: this.materialId,
            needs_check_in: this.needsCheckIn,
            order_by: this.orderBy,
            order_name: this.orderName,
            ordered: this.ordered,
            ordered_updated: this.orderedUpdated,
            project_id: this.projectId,
            quantity: this.quantity,
            required_on_jobsite: this.requiredOnJobsite,
            required_on_jobsite_updated: this.requiredOnJobsiteUpdated,
            serial_number: this.serialNumber,
            shakeout_days: this.shakeoutDays,
            status: this.status,
            submit_by: this.submitBy,
            updated_at: this.updatedAt,
            activities_orders_attributes: this.activitiesOrders
                ?.map((item) => item.toApiObject(visited))
                .filter((item) => item !== undefined),
            audits: this.audits?.map((item) => item.toApiObject(visited)).filter((item) => item !== undefined),
            comments: this.comments?.map((item) => item.toApiObject(visited)).filter((item) => item !== undefined),
            delivery_attributes: this.delivery?.toApiObject(visited),
            material_attributes: this.material?.toApiObject(visited),
            order_check_ins: this.orderCheckIns
                ?.map((item) => item.toApiObject(visited))
                .filter((item) => item !== undefined),
            project_attributes: this.project?.toApiObject(visited),
            _destroy: this._destroy,
        };
    }

    // NOTE: This could potentially cause infinite loops if the object graph is
    // circular.
    inAlternateTimezone(timezone: string): Order {
        const clone = Object.create(this) as Order;

        for (const key of Object.keys(this)) {
            if (this[key] instanceof dayjs) {
                // @ts-expect-error: Typescript thinks that this is not a dayjs object even though it is checked on the line before
                clone[key] = this[key].tz(timezone);
            } else if (Array.isArray(this[key])) {
                clone[key] = this[key].map((item) =>
                    item.inAlternateTimezone ? item.inAlternateTimezone(timezone) : item
                );
            } else if (this[key] && this[key].inAlternateTimezone) {
                clone[key] = this[key].inAlternateTimezone(timezone);
            }
        }

        return clone;
    }
}
